import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Wallet } from "lucide-react";

import { billingApi } from "@/features/billing/api";
import { hasMinRole, useAuthStore } from "@/features/auth/store";
import { cn } from "@/lib/utils";

/** Vapi-style prepaid balance pill in the top bar. Owner/admin only (they
 *  are the ones who can buy credit); hidden for tenants not on the prepaid
 *  per-minute model. Links to the Billing page. */
export default function CreditChip() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = hasMinRole(user?.role, "admin");

  const creditQ = useQuery({
    queryKey: ["billing", "credit"],
    queryFn: billingApi.credit,
    enabled: isAdmin,
    staleTime: 30_000,
    refetchInterval: (q) => (q.state.status === "error" ? false : 60_000),
  });

  if (!isAdmin || !creditQ.data?.enabled) return null;

  const balance = creditQ.data.balance_cents ?? 0;
  const low = creditQ.data.low_threshold_cents ?? 0;
  const empty = balance <= 0;
  const isLow = !empty && low > 0 && balance < low;

  return (
    <Link
      to="/billing?buy=credit"
      title="Prepaid call credit — click to top up"
      className={cn(
        "hidden items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium tabular-nums transition-colors sm:flex",
        empty
          ? "border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-400"
          : isLow
          ? "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-400"
          : "border-border bg-muted/60 text-foreground hover:bg-muted",
      )}
    >
      <Wallet className="size-3.5" />
      <span className="text-muted-foreground">PAYG</span>
      ${(Math.max(balance, 0) / 100).toFixed(2)}
    </Link>
  );
}
